import { useId, useState, type FormEvent, type ReactNode } from 'react';

import type { CustomerDetailsPatch } from '../api/endpoints/customerFiles';
import type { CustomerLevel, CustomerProfile } from '../api/schemas';
import {
  LEVEL_LABEL,
  LEVELS,
  MAX_INJURIES_LENGTH,
  MAX_MUSIC_LENGTH,
  type FileVocabulary,
} from '../lib/customerFile';
import { CustomerPauseRow, escapeTo } from './CustomerPause';
import { FormField } from './FormField';

export type GlanceDetails = Pick<CustomerProfile, 'injuries' | 'music' | 'favoriteStaffId' | 'pause'>;

/** Somebody on the business's active roster, as the business detail lists them. */
export type RosterMember = { id: string; name: string };

type CustomerGlanceProps = {
  customerId: string;
  vocabulary: FileVocabulary;
  level: CustomerLevel | null;
  details: GlanceDetails;
  roster: readonly RosterMember[];
  /** A tap on a level: the editor clears it when it is the one already set. */
  onLevel: (level: CustomerLevel) => void;
  /** One changed field; the editor saves it beside the level and tags. */
  onSave: (patch: CustomerDetailsPatch) => void;
};

/** A favorite who is no longer on the active roster (left, or away on vacation). */
const OFF_ROSTER = 'No está en el equipo activo';

/**
 * The top of the ficha: what a coach reads before class without opening a
 * note. A level saves on the tap; injuries, music and the favorite coach each
 * open a small edit in place; the pause sits last. The class fields show only
 * where people train; every vertical can pause a client.
 */
export function CustomerGlance({ customerId, vocabulary, level, details, roster, onLevel, onSave }: CustomerGlanceProps) {
  return (
    <div className="group rows customer-glance" role="group" aria-label="De un vistazo">
      {vocabulary.levels ? <LevelRow customerId={customerId} level={level} onLevel={onLevel} /> : null}
      {vocabulary.glance ? (
        <>
          <TextRow
            label="Lesiones"
            editLabel="Editar lesiones"
            empty="Sin lesiones anotadas"
            value={details.injuries}
            maxLength={MAX_INJURIES_LENGTH}
            isMultiline
            onSave={(injuries) => onSave({ injuries })}
          />
          <TextRow
            label="Música"
            editLabel="Editar música"
            empty="Sin preferencia"
            value={details.music}
            maxLength={MAX_MUSIC_LENGTH}
            onSave={(music) => onSave({ music })}
          />
          <CoachRow
            value={details.favoriteStaffId}
            roster={roster}
            onSave={(favoriteStaffId) => onSave({ favoriteStaffId })}
          />
        </>
      ) : null}
      <CustomerPauseRow pause={details.pause} onSave={(pause) => onSave({ pause })} />
    </div>
  );
}

function LevelRow({
  customerId,
  level,
  onLevel,
}: {
  customerId: string;
  level: CustomerLevel | null;
  onLevel: (level: CustomerLevel) => void;
}) {
  const labelId = `level-${customerId}`;
  return (
    <div className="customer-glance-level">
      <p className="customer-file-label" id={labelId}>
        Nivel
      </p>
      <div className="seg customer-level" role="group" aria-labelledby={labelId}>
        {LEVELS.map((entry) => (
          <button key={entry} type="button" aria-pressed={level === entry} onClick={() => onLevel(entry)}>
            {LEVEL_LABEL[entry]}
          </button>
        ))}
      </div>
      <p className="row-sub customer-file-hint">{level ? 'Toca el nivel otra vez para quitarlo.' : 'Sin nivel todavía.'}</p>
    </div>
  );
}

type TextRowProps = {
  label: string;
  editLabel: string;
  empty: string;
  value: string | null;
  maxLength: number;
  isMultiline?: boolean;
  /** Trimmed; null when left blank. Called only when the value changed. */
  onSave: (value: string | null) => void;
};

function TextRow({ label, editLabel, empty, value, maxLength, isMultiline = false, onSave }: TextRowProps) {
  const id = useId();
  // null while reading; the text being edited otherwise.
  const [draft, setDraft] = useState<string | null>(null);

  if (draft === null) {
    return (
      <ReadRow label={label} value={value} empty={empty}>
        <button type="button" className="btn small ghost" aria-label={editLabel} onClick={() => setDraft(value ?? '')}>
          Editar
        </button>
      </ReadRow>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = draft.trim() || null;
    setDraft(null);
    if (next !== value) onSave(next);
  };
  const fieldProps = {
    id,
    className: 'field-input',
    value: draft,
    maxLength,
    autoFocus: true,
    onChange: (event: { target: { value: string } }) => setDraft(event.target.value),
  };

  return (
    <form className="customer-glance-edit" onSubmit={submit} onKeyDown={escapeTo(() => setDraft(null))}>
      <FormField label={label} htmlFor={id} hint={`Hasta ${maxLength} caracteres.`}>
        {isMultiline ? <textarea rows={2} {...fieldProps} /> : <input {...fieldProps} />}
      </FormField>
      <EditActions onCancel={() => setDraft(null)} />
    </form>
  );
}

function CoachRow({
  value,
  roster,
  onSave,
}: {
  value: string | null;
  roster: readonly RosterMember[];
  onSave: (staffId: string | null) => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const isOffRoster = value !== null && !roster.some((member) => member.id === value);
  const name = value === null ? null : (roster.find((member) => member.id === value)?.name ?? OFF_ROSTER);

  if (draft === null) {
    return (
      <ReadRow label="Coach favorito" value={name} empty="Sin coach favorito">
        {roster.length > 0 || value !== null ? (
          <button
            type="button"
            className="btn small ghost"
            aria-label="Editar coach favorito"
            onClick={() => setDraft(value ?? '')}
          >
            Editar
          </button>
        ) : null}
      </ReadRow>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = draft || null;
    setDraft(null);
    if (next !== value) onSave(next);
  };

  return (
    <form className="customer-glance-edit" onSubmit={submit} onKeyDown={escapeTo(() => setDraft(null))}>
      <FormField label="Coach favorito" htmlFor={id}>
        <select id={id} className="field-input" value={draft} autoFocus onChange={(event) => setDraft(event.target.value)}>
          <option value="">Sin coach favorito</option>
          {isOffRoster ? <option value={value}>{OFF_ROSTER}</option> : null}
          {roster.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}
            </option>
          ))}
        </select>
      </FormField>
      <EditActions onCancel={() => setDraft(null)} />
    </form>
  );
}

/** A label, its value (or a quiet "not yet"), and the row's action. */
function ReadRow({
  label,
  value,
  empty,
  children,
}: {
  label: string;
  value: string | null;
  empty: string;
  children: ReactNode;
}) {
  return (
    <div className="row">
      <div className="grow">
        <p className="customer-file-label">{label}</p>
        {value ? <p className="row-title">{value}</p> : <p className="row-sub">{empty}</p>}
      </div>
      {children}
    </div>
  );
}

function EditActions({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="row-actions">
      <button type="submit" className="btn small">
        Guardar
      </button>
      <button type="button" className="btn small secondary" onClick={onCancel}>
        Cancelar
      </button>
    </div>
  );
}
